import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Music, Search } from "lucide-react";

const FALLBACK_100 = Array.from({ length: 100 }, (_, i) => ({
    no: i + 1,
    name: `Bài ${i + 1}`,
    url: "",
}));

async function fetchLessons() {
    const r = await fetch("/api/drum-lessons", { credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.message || "Fetch failed");
    return data?.items || [];
}

export default function BeHocTrongLucLacPage() {
    const [q, setQ] = useState("");
    const [items, setItems] = useState(FALLBACK_100);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setErr("");
                const rows = await fetchLessons(); // [{no,name,url}]
                const map = new Map(rows.map((x) => [Number(x.no), x]));
                const merged = FALLBACK_100.map((x) => {
                    const fromDb = map.get(x.no);
                    return fromDb
                        ? { ...x, ...fromDb, url: (fromDb.url || "").trim() }
                        : x;
                });
                if (alive) setItems(merged);
            } catch (e) {
                if (alive) setErr("Không tải được danh sách bài học.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    const filtered = useMemo(() => {
        const kw = (q || "").trim().toLowerCase();
        if (!kw) return items;
        return items.filter(
            (x) =>
                String(x.no).includes(kw) ||
                (x.name || "").toLowerCase().includes(kw)
        );
    }, [q, items]);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="mx-auto w-full max-w-5xl px-4 pb-10 pt-4">
                <nav className="mb-3 text-sm text-slate-500">
                    <Link to="/" className="hover:text-slate-700">Trang chủ</Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-700">Bé học trống lục lạc</span>
                </nav>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50">
                            <Music className="h-6 w-6 text-rose-600" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="truncate text-lg font-semibold text-rose-700">
                                Bé Học Trống Lục Lạc
                            </h1>
                            <p className="text-sm text-slate-500">
                                Học theo video YouTube – từ cơ bản đến thành thạo
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <Search className="h-4 w-4 text-slate-500" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Tìm bài học... (nhập số hoặc tên)"
                            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                        />
                    </div>

                    {loading && <div className="mt-3 text-sm text-slate-500">Đang tải…</div>}
                    {err && <div className="mt-3 text-sm text-rose-600">{err}</div>}
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-bold text-slate-800">Chọn link học:</h2>
                        <div className="text-sm text-slate-500">{filtered.length} / 100</div>
                    </div>

                    <div className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
                        {filtered.map((x) => {
                            const hasUrl = Boolean((x.url || "").trim());
                            return (
                                <div key={x.no} className="flex items-center justify-between gap-3 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-800">
                                            {x.no}. <span className="font-medium">{x.name}</span>
                                            <span className="font-normal text-slate-400"> : </span>
                                            <span className="font-normal text-slate-600">
                                                {hasUrl ? "link liên kết qua YouTube" : "chưa có link"}
                                            </span>
                                        </div>
                                        {hasUrl && <div className="truncate text-xs text-slate-400">{x.url}</div>}
                                    </div>

                                    <a
                                        href={hasUrl ? x.url : undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={[
                                            "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold",
                                            hasUrl
                                                ? "bg-rose-600 text-white hover:bg-rose-700"
                                                : "cursor-not-allowed bg-slate-100 text-slate-400",
                                        ].join(" ")}
                                        onClick={(e) => {
                                            if (!hasUrl) e.preventDefault();
                                        }}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Mở
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
